// Carousel Cards Builder:
// Manages 2 to 10 cards per Meta specification:
// Each card: Media (Image or Video), body (<= 160 chars), <= 2 action buttons.
// Supports Product Carousel integration with `useProducts()`.

import { useI18n } from '../../lib/i18n';
import { useProducts, type Product } from '../../lib/catalog';
import MediaUploader from './MediaUploader';
import type { TplButton, TplCarouselCard } from '../../lib/templateTypes';

export interface CarouselCardsBuilderProps {
  cards: TplCarouselCard[];
  onChange: (cards: TplCarouselCard[]) => void;
  isProductCarousel?: boolean;
  integrationId?: string;
  disabled?: boolean;
}

export default function CarouselCardsBuilder({
  cards,
  onChange,
  isProductCarousel = false,
  integrationId,
  disabled = false,
}: CarouselCardsBuilderProps) {
  const { t } = useI18n();
  const { products } = useProducts();

  function addCard() {
    if (cards.length >= 10) return;
    const newCard: TplCarouselCard = {
      id: `card_${Date.now()}_${cards.length + 1}`,
      mediaType: 'IMAGE',
      bodyText: '',
      buttons: [{ type: 'URL', text: 'View Details', url: 'https://example.com' }],
    };
    onChange([...cards, newCard]);
  }

  function removeCard(idx: number) {
    if (cards.length <= 2) return;
    onChange(cards.filter((_, i) => i !== idx));
  }

  function updateCard(idx: number, patch: Partial<TplCarouselCard>) {
    onChange(cards.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  }

  function addCardButton(cardIdx: number) {
    const card = cards[cardIdx];
    if (!card || card.buttons.length >= 2) return;
    const newBtn: TplButton = { type: 'QUICK_REPLY', text: 'Quick Reply' };
    updateCard(cardIdx, { buttons: [...card.buttons, newBtn] });
  }

  function updateCardButton(cardIdx: number, btnIdx: number, patch: Partial<TplButton>) {
    const card = cards[cardIdx];
    if (!card) return;
    const updatedButtons = card.buttons.map((b, i) => (i === btnIdx ? { ...b, ...patch } : b));
    updateCard(cardIdx, { buttons: updatedButtons });
  }

  function removeCardButton(cardIdx: number, btnIdx: number) {
    const card = cards[cardIdx];
    if (!card) return;
    updateCard(cardIdx, { buttons: card.buttons.filter((_, i) => i !== btnIdx) });
  }

  function applyProductToCard(cardIdx: number, product: Product) {
    updateCard(cardIdx, {
      bodyText: `${product.name} - ${product.price} ${product.currency}`,
      buttons: [
        {
          type: 'URL',
          text: 'Buy Now',
          url: `https://example.com/product/${product.id}`,
        },
      ],
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
            {t('tpl.carousel.title') || 'Carousel Cards'}
          </h4>
          <p className="text-[11px] text-slate-500">
            {t('tpl.carousel.rules') || 'Meta requires 2 to 10 cards. Each card has media, body (max 160 chars), and up to 2 buttons.'}
          </p>
        </div>
        <button
          type="button"
          onClick={addCard}
          disabled={disabled || cards.length >= 10}
          className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 hover:bg-indigo-100 disabled:opacity-40"
        >
          + {t('tpl.carousel.addCard') || 'Add Card'} ({cards.length}/10)
        </button>
      </div>

      {cards.length < 2 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-700 font-medium">
          ⚠️ Meta requires a minimum of 2 cards for carousel templates. Please add at least {2 - cards.length} more card.
        </div>
      )}

      <div className="space-y-4">
        {cards.map((card, idx) => (
          <div
            key={card.id || idx}
            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3"
          >
            {/* Card Header & Controls */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <span className="font-bold text-xs text-slate-800">
                Card #{idx + 1}
              </span>

              <div className="flex items-center gap-2">
                {isProductCarousel && products.length > 0 && (
                  <select
                    onChange={(e) => {
                      const prod = products.find((p) => p.id === e.target.value);
                      if (prod) applyProductToCard(idx, prod);
                    }}
                    defaultValue=""
                    className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-700"
                  >
                    <option value="" disabled>
                      Select Catalog Product...
                    </option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.price} {p.currency})
                      </option>
                    ))}
                  </select>
                )}

                <button
                  type="button"
                  onClick={() => removeCard(idx)}
                  disabled={cards.length <= 2}
                  className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:opacity-30"
                  title={cards.length <= 2 ? 'Minimum 2 cards required' : 'Remove card'}
                >
                  ✕ Remove
                </button>
              </div>
            </div>

            {/* Media Selector */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] font-semibold text-slate-600">
                  Card Media (Image / Video)
                </label>
                <div className="flex gap-2">
                  <label className="flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer">
                    <input
                      type="radio"
                      name={`card_media_type_${idx}`}
                      checked={card.mediaType === 'IMAGE'}
                      onChange={() => updateCard(idx, { mediaType: 'IMAGE' })}
                    />
                    <span>Image</span>
                  </label>
                  <label className="flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer">
                    <input
                      type="radio"
                      name={`card_media_type_${idx}`}
                      checked={card.mediaType === 'VIDEO'}
                      onChange={() => updateCard(idx, { mediaType: 'VIDEO' })}
                    />
                    <span>Video</span>
                  </label>
                </div>
              </div>

              <MediaUploader
                mediaType={card.mediaType}
                value={
                  card.mediaHandle
                    ? {
                        handle: card.mediaHandle,
                        mime: card.mediaType === 'IMAGE' ? 'image/jpeg' : 'video/mp4',
                        size: 0,
                        storagePath: card.mediaStoragePath || '',
                        createdAt: Date.now(),
                      }
                    : null
                }
                onChange={(media) => {
                  updateCard(idx, {
                    mediaHandle: media?.handle,
                    mediaStoragePath: media?.storagePath,
                  });
                }}
                integrationId={integrationId}
                disabled={disabled}
              />
            </div>

            {/* Card Body Text */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-[11px] font-semibold text-slate-600">
                  Card Body Text
                </label>
                <span
                  className={`text-[10px] ${
                    card.bodyText.length > 160 ? 'text-red-600 font-bold' : 'text-slate-400'
                  }`}
                >
                  {card.bodyText.length}/160
                </span>
              </div>
              <textarea
                value={card.bodyText}
                maxLength={160}
                rows={2}
                onChange={(e) => updateCard(idx, { bodyText: e.target.value })}
                placeholder="Brief description for this card..."
                className="w-full rounded-lg border border-slate-200 p-2 text-xs outline-none focus:border-indigo-500"
              />
            </div>

            {/* Card Buttons */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-semibold text-slate-600">
                  Card Buttons (max 2)
                </span>
                {card.buttons.length < 2 && (
                  <button
                    type="button"
                    onClick={() => addCardButton(idx)}
                    className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800"
                  >
                    + Add Button
                  </button>
                )}
              </div>

              <div className="space-y-2">
                {card.buttons.map((btn, bIdx) => (
                  <div
                    key={bIdx}
                    className="flex items-center gap-2 rounded-lg bg-slate-50 p-2 border border-slate-200"
                  >
                    <select
                      value={btn.type}
                      onChange={(e) =>
                        updateCardButton(idx, bIdx, {
                          type: e.target.value as 'QUICK_REPLY' | 'URL',
                        })
                      }
                      className="rounded border border-slate-200 bg-white px-2 py-1 text-xs"
                    >
                      <option value="URL">Visit Website</option>
                      <option value="QUICK_REPLY">Quick Reply</option>
                    </select>

                    <input
                      type="text"
                      value={btn.text}
                      placeholder="Button text (max 25)"
                      maxLength={25}
                      onChange={(e) => updateCardButton(idx, bIdx, { text: e.target.value })}
                      className="flex-1 rounded border border-slate-200 px-2 py-1 text-xs outline-none"
                    />

                    {btn.type === 'URL' && (
                      <input
                        type="text"
                        value={btn.url || ''}
                        placeholder="https://..."
                        onChange={(e) => updateCardButton(idx, bIdx, { url: e.target.value })}
                        className="flex-1 rounded border border-slate-200 px-2 py-1 text-xs font-mono outline-none"
                      />
                    )}

                    <button
                      type="button"
                      onClick={() => removeCardButton(idx, bIdx)}
                      className="h-6 w-6 text-slate-400 hover:text-red-600"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
